import type { GeometryQuestion } from './geometryTestNY';

export const geometryTestOH: GeometryQuestion[] = [
  {
    id: 1, type: 'multiple-choice', points: 2,
    topic: 'Rigid Motions', standard: 'OH.GEO.G.2',
    question: 'Which transformation preserves both shape AND size (is an isometry)?',
    options: ['Dilation', 'Rotation', 'Horizontal stretch', 'Shear'],
    correctAnswer: 1,
    solution: [
      'An isometry is a transformation that preserves distance (and therefore shape and size).',
      'Rotations, reflections, and translations are all isometries.',
      'Dilations change size; stretches and shears distort shape.',
      'Answer: Rotation'
    ]
  },
  {
    id: 2, type: 'multiple-choice', points: 2,
    topic: 'Triangle Congruence', standard: 'OH.GEO.G.2',
    question: 'Triangles PQR and XYZ have PQ = XY, QR = YZ, and PR = XZ. Which postulate proves them congruent?',
    options: ['SAS', 'ASA', 'SSS', 'AAS'],
    correctAnswer: 2,
    solution: [
      'SSS (Side-Side-Side): if all three pairs of corresponding sides are congruent, the triangles are congruent.',
      'All three sides are given as equal: PQ=XY, QR=YZ, PR=XZ.',
      'Answer: SSS'
    ]
  },
  {
    id: 3, type: 'multiple-choice', points: 2,
    topic: 'Angle Bisector', standard: 'OH.GEO.G.3',
    question: 'The angle bisector of angle A in triangle ABC divides the opposite side BC in the ratio AB:AC. If AB = 6, AC = 9, and BC = 15, what is BD (where D is on BC)?',
    options: ['6', '9', '10', '5'],
    correctAnswer: 0,
    solution: [
      'Angle Bisector Theorem: BD/DC = AB/AC = 6/9 = 2/3',
      'BD + DC = 15',
      'BD = (2/5) × 15 = 6',
      'Answer: BD = 6'
    ]
  },
  {
    id: 4, type: 'multiple-choice', points: 2,
    topic: 'Pythagorean Theorem', standard: 'OH.GEO.G.7',
    question: 'A ladder 13 feet long leans against a wall. The base is 5 feet from the wall. How high up the wall does the ladder reach?',
    options: ['8 ft', '12 ft', '10 ft', '√194 ft'],
    correctAnswer: 1,
    solution: [
      'Use the Pythagorean Theorem: a² + b² = c²',
      '5² + h² = 13²',
      '25 + h² = 169',
      'h² = 144',
      'h = 12 feet',
      'Answer: 12 ft'
    ]
  },
  {
    id: 5, type: 'multiple-choice', points: 2,
    topic: 'Trigonometry', standard: 'OH.GEO.G.7',
    question: 'In right triangle DEF with right angle at F, tan(D) = 3/4. If EF = 12, what is DF?',
    options: ['9', '16', '15', '20'],
    correctAnswer: 1,
    solution: [
      'tan(D) = opposite/adjacent = EF/DF',
      '3/4 = 12/DF',
      'DF = 12 × 4/3 = 16',
      'Answer: DF = 16'
    ]
  },
  {
    id: 6, type: 'multiple-choice', points: 2,
    topic: 'Circle Chords', standard: 'OH.GEO.G.9',
    question: 'Two chords intersect inside a circle. One chord is divided into segments of 4 and 6. The other chord has one segment of 3. What is the other segment?',
    options: ['7', '8', '9', '12'],
    correctAnswer: 1,
    solution: [
      'Intersecting Chords Theorem: segment1 × segment2 = segment3 × segment4',
      '4 × 6 = 3 × x',
      '24 = 3x',
      'x = 8',
      'Answer: 8'
    ]
  },
  {
    id: 7, type: 'multiple-choice', points: 2,
    topic: 'Volume', standard: 'OH.GEO.G.13',
    question: 'What is the volume of a sphere with radius 3?',
    options: ['12π', '36π', '9π', '27π'],
    correctAnswer: 1,
    solution: [
      'Volume of a sphere = (4/3)πr³',
      'V = (4/3)π(3)³',
      'V = (4/3)π(27)',
      'V = 36π',
      'Answer: 36π'
    ]
  },
  {
    id: 8, type: 'multiple-choice', points: 2,
    topic: 'Coordinate Geometry', standard: 'OH.GEO.G.12',
    question: 'What is the slope of the line passing through (−3, 7) and (5, −1)?',
    options: ['−1', '1', '−2', '2'],
    correctAnswer: 0,
    solution: [
      'Slope = (y₂ − y₁) / (x₂ − x₁)',
      'm = (−1 − 7) / (5 − (−3))',
      'm = −8 / 8',
      'm = −1',
      'Answer: −1'
    ]
  },
  {
    id: 9, type: 'multiple-choice', points: 2,
    topic: 'Similarity', standard: 'OH.GEO.G.6',
    question: 'Triangle ABC ~ Triangle DEF with ratio 2:5. If AB = 8, what is DE?',
    options: ['3.2', '20', '10', '16'],
    correctAnswer: 1,
    solution: [
      'Corresponding sides of similar triangles are proportional.',
      'AB/DE = 2/5',
      '8/DE = 2/5',
      'DE = 8 × 5/2 = 20',
      'Answer: DE = 20'
    ]
  },
  {
    id: 10, type: 'multiple-choice', points: 2,
    topic: 'Transformations', standard: 'OH.GEO.G.1',
    question: 'Point Q(−2, 5) is rotated 90° counterclockwise about the origin. What are the new coordinates?',
    options: ['(5, 2)', '(−5, −2)', '(2, 5)', '(−5, 2)'],
    correctAnswer: 3,
    solution: [
      'Rule for 90° counterclockwise rotation: (x, y) → (−y, x)',
      'Q(−2, 5) → (−5, −2)',
      'Wait — let\'s recheck: (x,y) → (−y, x): (−2, 5) → (−5, −2)',
      'Answer: (−5, −2)'
    ]
  },
  {
    id: 11, type: 'multiple-choice', points: 2,
    topic: 'Midsegment Theorem', standard: 'OH.GEO.G.6',
    question: 'The midsegment of a triangle connects the midpoints of two sides. If the base of the triangle is 18, what is the length of the midsegment?',
    options: ['18', '9', '6', '36'],
    correctAnswer: 1,
    solution: [
      'Triangle Midsegment Theorem: the midsegment is parallel to the base and half its length.',
      'Midsegment = 18 / 2 = 9',
      'Answer: 9'
    ]
  },
  {
    id: 12, type: 'multiple-choice', points: 2,
    topic: 'Geometric Constructions', standard: 'OH.GEO.G.4',
    question: 'Which tool is used to construct a perpendicular bisector of a segment?',
    options: ['Protractor only', 'Ruler only', 'Compass and straightedge', 'Calculator'],
    correctAnswer: 2,
    solution: [
      'A perpendicular bisector is constructed using a compass and straightedge.',
      'Open the compass to more than half the segment length.',
      'Draw arcs from both endpoints; connect the intersection points.',
      'Answer: Compass and straightedge'
    ]
  },
  {
    id: 13, type: 'multiple-choice', points: 2,
    topic: 'Area of Triangle', standard: 'OH.GEO.G.13',
    question: 'A triangle has base 14 and height 9. What is its area?',
    options: ['126', '63', '46', '23'],
    correctAnswer: 1,
    solution: [
      'Area of a triangle = (1/2) × base × height',
      'A = (1/2) × 14 × 9',
      'A = (1/2) × 126 = 63',
      'Answer: 63'
    ]
  },
  {
    id: 14, type: 'multiple-choice', points: 2,
    topic: 'Parallel Lines Proof', standard: 'OH.GEO.G.3',
    question: 'If alternate interior angles formed by a transversal and two lines are congruent, then the two lines are:',
    options: ['Perpendicular', 'Parallel', 'Skew', 'Intersecting'],
    correctAnswer: 1,
    solution: [
      'The Converse of the Alternate Interior Angles Theorem states:',
      'If alternate interior angles are congruent, then the lines are parallel.',
      'Answer: Parallel'
    ]
  },
  {
    id: 15, type: 'multiple-choice', points: 2,
    topic: 'Modeling with Geometry', standard: 'OH.GEO.G.15',
    question: 'A circular swimming pool has diameter 24 feet. What is the area of the pool in square feet?',
    options: ['144π', '576π', '48π', '24π'],
    correctAnswer: 0,
    solution: [
      'Diameter = 24, so radius = 12',
      'Area = πr² = π(12)² = 144π',
      'Answer: 144π sq ft'
    ]
  },
  {
    id: 16, type: 'multiple-choice', points: 2,
    topic: 'Cavalieri\'s Principle', standard: 'OH.GEO.G.13',
    question: 'Two solids have the same height and equal cross-sectional areas at every level. According to Cavalieri\'s Principle, they have:',
    options: ['Equal surface areas', 'Equal volumes', 'Equal base areas', 'Equal heights only'],
    correctAnswer: 1,
    solution: [
      'Cavalieri\'s Principle: if two solids have the same height and equal cross-sectional areas at every level, they have equal volumes.',
      'Answer: Equal volumes'
    ]
  },
  {
    id: 17, type: 'multiple-choice', points: 2,
    topic: 'Angle in Semicircle', standard: 'OH.GEO.G.9',
    question: 'An angle inscribed in a semicircle (its intercepted arc is a semicircle) always measures:',
    options: ['45°', '60°', '90°', '180°'],
    correctAnswer: 2,
    solution: [
      'A semicircle has an arc of 180°.',
      'By the Inscribed Angle Theorem: inscribed angle = (1/2) × intercepted arc',
      'Inscribed angle = (1/2) × 180° = 90°',
      'Answer: 90°'
    ]
  },
  {
    id: 18, type: 'multiple-choice', points: 2,
    topic: 'Exterior Angle of Polygon', standard: 'OH.GEO.G.3',
    question: 'What is the measure of each exterior angle of a regular octagon?',
    options: ['45°', '135°', '60°', '40°'],
    correctAnswer: 0,
    solution: [
      'The sum of exterior angles of any convex polygon = 360°.',
      'A regular octagon has 8 equal exterior angles.',
      'Each exterior angle = 360° / 8 = 45°',
      'Answer: 45°'
    ]
  },
  {
    id: 19, type: 'multiple-choice', points: 2,
    topic: 'Inverse Trig', standard: 'OH.GEO.G.7',
    question: 'In a right triangle, the opposite side is 7 and the hypotenuse is 14. What is the angle (to the nearest degree)?',
    options: ['30°', '45°', '60°', '90°'],
    correctAnswer: 0,
    solution: [
      'sin(θ) = opposite / hypotenuse = 7/14 = 0.5',
      'θ = sin⁻¹(0.5) = 30°',
      'Answer: 30°'
    ]
  },
  {
    id: 20, type: 'multiple-choice', points: 2,
    topic: 'Coordinate Geometry', standard: 'OH.GEO.G.12',
    question: 'What is the equation of the line through (0, 4) with slope −3?',
    options: ['y = −3x + 4', 'y = 3x − 4', 'y = −3x − 4', 'y = 4x − 3'],
    correctAnswer: 0,
    solution: [
      'Slope-intercept form: y = mx + b',
      'm = −3, b = 4 (y-intercept is the point (0, 4))',
      'y = −3x + 4',
      'Answer: y = −3x + 4'
    ]
  },
  {
    id: 21, type: 'multiple-choice', points: 2,
    topic: 'Rhombus Properties', standard: 'OH.GEO.G.3',
    question: 'In a rhombus, the diagonals:',
    options: [
      'Are equal in length',
      'Bisect each other at right angles',
      'Are parallel to each other',
      'Do not bisect each other'
    ],
    correctAnswer: 1,
    solution: [
      'In a rhombus, the diagonals bisect each other at right angles (90°).',
      'They are NOT necessarily equal in length (that would make it a square).',
      'Answer: Bisect each other at right angles'
    ]
  },
  {
    id: 22, type: 'multiple-choice', points: 2,
    topic: 'Surface Area of Prism', standard: 'OH.GEO.G.13',
    question: 'A rectangular prism has length 5, width 3, and height 4. What is its total surface area?',
    options: ['60', '94', '47', '120'],
    correctAnswer: 1,
    solution: [
      'SA = 2(lw + lh + wh)',
      'SA = 2(5×3 + 5×4 + 3×4)',
      'SA = 2(15 + 20 + 12)',
      'SA = 2(47) = 94',
      'Answer: 94'
    ]
  },
  {
    id: 23, type: 'multiple-choice', points: 2,
    topic: 'Geometric Mean', standard: 'OH.GEO.G.7',
    question: 'The altitude drawn to the hypotenuse of a right triangle creates two smaller triangles. If the hypotenuse segments are 4 and 9, what is the altitude?',
    options: ['6', '13', '36', '√13'],
    correctAnswer: 0,
    solution: [
      'Geometric Mean (Altitude) Theorem: altitude² = product of the two hypotenuse segments',
      'altitude² = 4 × 9 = 36',
      'altitude = √36 = 6',
      'Answer: 6'
    ]
  },
  {
    id: 24, type: 'multiple-choice', points: 2,
    topic: 'Rotation', standard: 'OH.GEO.G.1',
    question: 'Point M(4, −3) is rotated 180° about the origin. What are the new coordinates?',
    options: ['(3, 4)', '(−4, 3)', '(4, 3)', '(−3, −4)'],
    correctAnswer: 1,
    solution: [
      'Rule for 180° rotation about the origin: (x, y) → (−x, −y)',
      'M(4, −3) → M\'(−4, 3)',
      'Answer: (−4, 3)'
    ]
  },
  {
    id: 25, type: 'multiple-choice', points: 2,
    topic: 'Secant-Tangent', standard: 'OH.GEO.G.9',
    question: 'A tangent and a secant are drawn from an external point. The tangent segment is 6 and the external part of the secant is 3. What is the whole secant length?',
    options: ['9', '12', '18', '15'],
    correctAnswer: 1,
    solution: [
      'Tangent-Secant Theorem: tangent² = external × whole secant',
      '6² = 3 × whole',
      '36 = 3 × whole',
      'whole = 12',
      'Answer: 12'
    ]
  },
  {
    id: 26, type: 'multiple-choice', points: 2,
    topic: 'Trapezoid Midsegment', standard: 'OH.GEO.G.3',
    question: 'A trapezoid has parallel bases of 8 and 14. What is the length of the midsegment?',
    options: ['6', '11', '22', '7'],
    correctAnswer: 1,
    solution: [
      'Trapezoid Midsegment Theorem: midsegment = (base₁ + base₂) / 2',
      'Midsegment = (8 + 14) / 2 = 22 / 2 = 11',
      'Answer: 11'
    ]
  },
  {
    id: 27, type: 'multiple-choice', points: 2,
    topic: 'Locus', standard: 'OH.GEO.G.11',
    question: 'The set of all points equidistant from a fixed point is called a:',
    options: ['Line', 'Parabola', 'Circle', 'Ellipse'],
    correctAnswer: 2,
    solution: [
      'By definition, a circle is the set of all points in a plane equidistant from a fixed point (the center).',
      'Answer: Circle'
    ]
  },
  {
    id: 28, type: 'multiple-choice', points: 2,
    topic: 'Angle of Elevation', standard: 'OH.GEO.G.7',
    question: 'From a point 50 feet from the base of a building, the angle of elevation to the top is 60°. How tall is the building?',
    options: ['50√3', '50/√3', '25√3', '100'],
    correctAnswer: 0,
    solution: [
      'tan(60°) = height / 50',
      'tan(60°) = √3',
      'height = 50 × √3 = 50√3',
      'Answer: 50√3 feet'
    ]
  },
  {
    id: 29, type: 'multiple-choice', points: 2,
    topic: 'Composite Figures', standard: 'OH.GEO.G.13',
    question: 'A figure consists of a rectangle (6 × 4) with a semicircle on top (diameter = 6). What is the total area?',
    options: ['24 + 4.5π', '24 + 9π', '24 + 18π', '24 + 3π'],
    correctAnswer: 0,
    solution: [
      'Rectangle area = 6 × 4 = 24',
      'Semicircle: diameter = 6, so radius = 3',
      'Semicircle area = (1/2)πr² = (1/2)π(9) = 4.5π',
      'Total = 24 + 4.5π',
      'Answer: 24 + 4.5π'
    ]
  },
  {
    id: 30, type: 'multiple-choice', points: 2,
    topic: 'Proofs', standard: 'OH.GEO.G.3',
    question: 'In a two-column proof, what goes in the "Reasons" column?',
    options: [
      'The given information only',
      'Definitions, postulates, theorems, and given information',
      'Only theorems',
      'The conclusion only'
    ],
    correctAnswer: 1,
    solution: [
      'In a two-column proof, the Statements column lists what is true.',
      'The Reasons column justifies each statement using: given information, definitions, postulates, and theorems.',
      'Answer: Definitions, postulates, theorems, and given information'
    ]
  }
];
